<template>
  <div class="privacy">
    <div class="privacy-card">
      <h1 class="privacy-title">{{ t.title }}</h1>
      <p class="privacy-updated">{{ t.updated }}</p>

      <p v-for="(p, i) in t.intro" :key="`intro-${i}`" class="privacy-text">
        {{ p }}
      </p>

      <section
        v-for="section in t.sections"
        :key="section.id"
        :id="section.id"
        class="privacy-section"
      >
        <h2 class="privacy-heading">{{ section.title }}</h2>

        <template v-for="(block, i) in section.blocks" :key="`${section.id}-${i}`">
          <p v-if="typeof block === 'string'" class="privacy-text">
            {{ block }}
          </p>

          <ul v-else class="privacy-list">
            <li v-for="(item, j) in block" :key="j">{{ item }}</li>
          </ul>
        </template>
      </section>

      <section class="privacy-section">
        <h2 class="privacy-heading">{{ t.contacts.title }}</h2>
        <p class="privacy-text">
          {{ t.contacts.text }}
          <a class="privacy-link" :href="telegramSupport" target="_blank">{{ telegramSupport }}</a>
        </p>
        <p class="privacy-text">
          {{ t.contacts.tos }}
          <router-link class="privacy-link" to="/tos">{{ t.contacts.tosLink }}</router-link>
        </p>
      </section>
    </div>
  </div>
</template>

<script>
const TELEGRAM_SUPPORT = 'https://t.me/metrica_support'

const en = {
  title: 'Privacy Policy',
  updated: 'Last updated: September 6, 2026',
  intro: [
    'This policy describes what data is collected by the Metrica service and how it is used. It covers the website metrica.guru and pro.metrica.guru, the Metrica browser extension for Chrome and Firefox, and the Metrica Telegram bot (together, the "Service").',
    'Only the data needed to show website statistics, to run the account, and to protect the Service from abuse is collected. Personal data is not sold.',
  ],
  sections: [
    {
      id: 'extension',
      title: 'Browser extension',
      blocks: [
        'The extension shows traffic statistics for the website open in the current tab. It works only when its icon is clicked.',
        'When the extension popup is opened, the following is accessed:',
        [
          'The domain name of the active tab. It is sent to the Service API to look up statistics for that website. Page content is not read, browsing history is not recorded and nothing runs in the background.',
          'The "jwt" authentication cookie of pro.metrica.guru, if the user is signed in on the website. It is used only to authenticate requests to the Service API. Cookies of other websites are not read.',
          'The language preference, which is stored locally in the browser and is never sent anywhere.',
        ],
        'For signed-in users, the domains looked up are linked to the account: they appear in the history on the website and count towards the daily and monthly lookup limit.',
        'To display website icons, favicons are requested from Google (google.com/s2/favicons), so the domain names shown in the popup are sent to Google. The extension contains no analytics or tracking code.',
        'Permissions requested by the extension: "activeTab" to learn the domain of the current tab when the icon is clicked, "cookies" and host access to pro.metrica.guru to read the authentication cookie and call the Service API.',
      ],
    },
    {
      id: 'account',
      title: 'Account data',
      blocks: [
        'Sign-in is available with Google or Telegram. Depending on the method, the following is received and stored:',
        [
          'Google: the Google account identifier and email address.',
          'Telegram: the Telegram identifier, first and last name and username.',
        ],
        'The interface language, the registration date and the date of last activity are also stored. This data is used to identify the user and to contact the user about the Service.',
        'Information received from Google APIs is used in accordance with the Google API Services User Data Policy, including the Limited Use requirements.',
      ],
    },
    {
      id: 'usage',
      title: 'Usage data',
      blocks: [
        [
          'Websites looked up on the website, in the extension or in the bot are linked to the account and shown in the history.',
          'Daily and monthly lookup counters per account. They are stored in a cache and expire automatically at the end of the day or month.',
          'The IP address of requests, used only to limit abuse. Counters are kept in memory and reset daily.',
          'Standard server logs (request time, address, response code), kept for a limited time for diagnostics.',
        ],
      ],
    },
    {
      id: 'analytics',
      title: 'Website analytics',
      blocks: [
        'Amplitude is used on the website to understand how the interface is used (pages opened, buttons clicked). Amplitude receives a device identifier and, for signed-in users, an internal user identifier. Amplitude is not used in the browser extension.',
      ],
    },
    {
      id: 'sharing',
      title: 'Sharing',
      blocks: [
        'Personal data is not sold and is not shared with third parties for their own purposes. Data is passed only to processors that run the Service: hosting, Amplitude and Google as described above, and when required by law.',
      ],
    },
    {
      id: 'retention',
      title: 'Retention and user rights',
      blocks: [
        'Account data is kept while the account exists. Lookup counters expire automatically. A copy of the data or deletion of the account can be requested at any time using the contact below; the account and its data are deleted within 30 days.',
      ],
    },
    {
      id: 'security',
      title: 'Security',
      blocks: [
        'All traffic between the browser, the extension and the Service servers is encrypted with HTTPS. Access to servers and databases is restricted to the team operating the Service.',
      ],
    },
    {
      id: 'changes',
      title: 'Changes',
      blocks: [
        'This policy may be updated. The current version is always available on this page, the date of the last change is shown at the top.',
      ],
    },
  ],
  contacts: {
    title: 'Contact',
    text: 'Questions about this policy, data access or deletion requests are accepted in Telegram:',
    tos: 'The rules of using the Service are set out in the',
    tosLink: 'Terms of Service',
  },
}

const ru = {
  title: 'Политика конфиденциальности',
  updated: 'Обновлено: 6 сентября 2026',
  intro: [
    'Эта политика описывает, какие данные собираются сервисом Metrica и как они используются. Она относится к сайту metrica.guru и pro.metrica.guru, браузерному расширению Metrica для Chrome и Firefox и Telegram-боту Metrica (вместе — «Сервис»).',
    'Собираются только данные, необходимые для показа статистики сайтов, работы аккаунта и защиты Сервиса от злоупотреблений. Персональные данные не продаются.',
  ],
  sections: [
    {
      id: 'extension',
      title: 'Браузерное расширение',
      blocks: [
        'Расширение показывает статистику посещаемости сайта, открытого в текущей вкладке. Оно работает только по клику на иконку.',
        'При открытии окна расширения используются:',
        [
          'Домен активной вкладки. Он отправляется в API Сервиса, чтобы найти статистику по этому сайту. Содержимое страниц не читается, история посещений не записывается, в фоне ничего не выполняется.',
          'Cookie авторизации «jwt» сайта pro.metrica.guru, если пользователь вошёл на сайте. Она используется только для авторизации запросов к API Сервиса. Cookie других сайтов не читаются.',
          'Выбранный язык интерфейса. Он хранится локально в браузере и никуда не отправляется.',
        ],
        'Для вошедших пользователей проверяемые домены привязываются к аккаунту: они видны в истории на сайте и учитываются в дневном и месячном лимите проверок.',
        'Для показа иконок сайтов фавиконки запрашиваются у Google (google.com/s2/favicons), поэтому домены, отображаемые в окне расширения, передаются Google. Аналитики и трекинга в расширении нет.',
        'Разрешения, которые запрашивает расширение: «activeTab», чтобы узнать домен текущей вкладки при клике на иконку, «cookies» и доступ к pro.metrica.guru, чтобы прочитать cookie авторизации и обращаться к API Сервиса.',
      ],
    },
    {
      id: 'account',
      title: 'Данные аккаунта',
      blocks: [
        'Вход доступен через Google или Telegram. В зависимости от способа получаются и хранятся:',
        [
          'Google: идентификатор аккаунта Google и адрес электронной почты.',
          'Telegram: идентификатор Telegram, имя, фамилия и username.',
        ],
        'Также хранятся язык интерфейса, дата регистрации и дата последней активности. Эти данные нужны для идентификации пользователя и связи с пользователем по вопросам Сервиса.',
        'Информация, полученная через Google API, используется в соответствии с политикой Google API Services User Data Policy, включая требования Limited Use.',
      ],
    },
    {
      id: 'usage',
      title: 'Данные об использовании',
      blocks: [
        [
          'Сайты, проверенные на сайте, в расширении или в боте, привязываются к аккаунту и показываются в истории.',
          'Счётчики проверок за день и за месяц по аккаунту. Они хранятся в кэше и автоматически сбрасываются в конце дня или месяца.',
          'IP-адрес запросов, только для ограничения злоупотреблений. Счётчики хранятся в памяти и сбрасываются ежедневно.',
          'Стандартные серверные логи (время запроса, адрес, код ответа), которые хранятся ограниченное время для диагностики.',
        ],
      ],
    },
    {
      id: 'analytics',
      title: 'Аналитика на сайте',
      blocks: [
        'На сайте используется Amplitude, чтобы понимать, как используется интерфейс (открытые страницы, нажатые кнопки). Amplitude получает идентификатор устройства, а для вошедших пользователей — внутренний идентификатор пользователя. В браузерном расширении Amplitude не используется.',
      ],
    },
    {
      id: 'sharing',
      title: 'Передача данных',
      blocks: [
        'Персональные данные не продаются и не передаются третьим лицам для их собственных целей. Данные передаются только исполнителям, обеспечивающим работу Сервиса: хостингу, Amplitude и Google, как описано выше, а также по требованию закона.',
      ],
    },
    {
      id: 'retention',
      title: 'Сроки хранения и права пользователя',
      blocks: [
        'Данные аккаунта хранятся, пока существует аккаунт. Счётчики проверок сбрасываются автоматически. Копию данных или удаление аккаунта можно запросить в любой момент по контакту ниже; аккаунт и его данные удаляются в течение 30 дней.',
      ],
    },
    {
      id: 'security',
      title: 'Безопасность',
      blocks: [
        'Весь трафик между браузером, расширением и серверами Сервиса шифруется по HTTPS. Доступ к серверам и базам данных есть только у команды, обслуживающей Сервис.',
      ],
    },
    {
      id: 'changes',
      title: 'Изменения',
      blocks: [
        'Политика может обновляться. Актуальная версия всегда доступна на этой странице, дата последнего изменения указана вверху.',
      ],
    },
  ],
  contacts: {
    title: 'Контакт',
    text: 'Вопросы по этой политике, запросы доступа к данным или их удаления принимаются в Telegram:',
    tos: 'Правила использования Сервиса описаны в',
    tosLink: 'Пользовательском соглашении',
  },
}

export default {
  computed: {
    telegramSupport() {
      return TELEGRAM_SUPPORT
    },
    t() {
      return this.$i18n.locale === 'ru' ? ru : en
    },
  },
}
</script>

<style scoped>
.privacy {
  width: 100%;
  padding: 0 30px 40px;
}

.privacy-card {
  max-width: 900px;
  background-color: var(--bg-surface);
  box-shadow: var(--shadow-page);
  border-radius: 7px;
  padding: 40px 45px;
}

.privacy-title {
  font-weight: 700;
  font-size: 28px;
  line-height: 36px;
  color: var(--text-primary);
  margin: 0;
}

.privacy-updated {
  margin: 8px 0 25px;
  font-size: 14px;
  color: var(--text-secondary2);
}

.privacy-section {
  margin-top: 30px;
}

.privacy-heading {
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: var(--text-primary);
  margin: 0 0 12px;
}

.privacy-text {
  font-size: 15px;
  line-height: 24px;
  color: var(--text-primary);
  margin: 0 0 12px;
}

.privacy-list {
  margin: 0 0 12px;
  padding-left: 22px;
  font-size: 15px;
  line-height: 24px;
  color: var(--text-primary);
}

.privacy-list li {
  margin-bottom: 6px;
}

.privacy-link {
  color: var(--main-color);
  text-decoration: none;
}

.privacy-link:hover {
  text-decoration: underline;
}

@media (max-width: 992px) {
  .privacy {
    padding: 0 10px 30px;
  }

  .privacy-card {
    padding: 25px 20px;
  }

  .privacy-title {
    font-size: 22px;
    line-height: 28px;
  }
}
</style>
